import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors PUT /progress-audit/progress/:progressId/certificate/fix
// (service-learning, SuperAdmin only) - creates the certificate for a
// course that shows courseProgress.progress:1 (finished) but never
// actually got one (SYSTEM.debt.md ítem 29/§15.29). Can come back with an
// explicit error (course not really finished, or not certificate-enabled),
// so the backend's message is surfaced instead of a generic one.
const DEFAULT_ERROR_MESSAGE = "No se pudo crear el certificado";

interface ProgressAuditFixCertificateState {
  status: FetchStatus;
}

const initialState: ProgressAuditFixCertificateState = {
  status: FetchStatus.Idle,
};

export const progressAuditFixCertificateSlice = createSlice({
  name: "progressAuditFixCertificate",
  initialState,
  reducers: {
    fixing: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fixed: (state) => {
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.status = FetchStatus.Error;
    },
  },
});

export const { fixing, fixed, error } = progressAuditFixCertificateSlice.actions;

export const fixProgressAuditCertificate = (
  progressId: string,
  onSuccess: () => void = () => {},
  onError: (message: string) => void = () => {}
) => (dispatch: Dispatch<object>) => {
  return dispatch(
    apiActionCreator({
      endpoint: `/progress-audit/progress/${progressId}/certificate/fix`,
      types: {
        requestType: fixing,
        successTypes: [
          {
            actionOrCreator: fixed,
            selector: () => {
              onSuccess();
            },
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
            selector: (response: { data?: { data?: string } }) => {
              onError(response?.data?.data || DEFAULT_ERROR_MESSAGE);
            },
          },
        ],
      },
      method: HttpMethods.PUT,
      microservice: Microservices.LEARNING_SERVICE,
      authenticated: true,
    })
  );
};

export const selectIsFixingProgressAuditCertificate = (state: RootState) =>
  state.progressAuditFixCertificate.status === FetchStatus.Fetching;

export default progressAuditFixCertificateSlice.reducer;
